/*
 * Copyright (c) 2025. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import {
  type AddFileResponse,
  type BidRequestInputApprovalsPayload,
  type BidRequestProjectRecord,
  type BidRequestQueryOptions,
  type BidRequestRecord,
  type BidRequestState,
  type BidRequestStatusOnEdit,
  type BidRequestsQueryListColumns,
  type ConfigsPerScenario,
  type EditBidRequestInputsPayload,
  type EditConfigsPayload,
  type EditProcurementInputsPayload,
  type FlowRunStatus,
  getBidRequestState,
  type ListResultsQuery,
  type MultiConfigPowerAlphaRequest,
  type NewBidRequestPayload,
  type NewSfPricingScenarioRecordPayload,
  type OpportunityListEntry,
  type PaMultiConfigSimResponse,
  type PricingDashboardQueryOptions,
  type PricingDashboardResponse,
  type PricingScenarioRecord,
  type SFObjectUpdateResponse,
  type TeamsPerProject,
  type UUID,
} from '@poweralpha/common';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { hoursToMilliseconds, minutesToMilliseconds } from 'date-fns';
import type { HTTPError, SearchParamsOption } from 'ky';
import { api, getRunsStatus } from '../api';
import { objectToQueryKey } from '../queryClient';
import type { MutationOptions, QueryOptions } from '../react-components/queries';

export async function getBidRequestTeams(
  opportunityId: string | undefined,
  id?: string,
  version?: number,
  addedProjects?: Array<string>,
): Promise<TeamsPerProject> {
  let url = `opportunities/${opportunityId}/bid-requests/teams`;
  if (id && version !== undefined) {
    url = `opportunities/${opportunityId}/bid-requests/${id}/${version}/teams`;
  }
  return api
    .get(
      `${url}${
        addedProjects?.length ? `?addedProject=${addedProjects.join('&addedProject=')}` : ''
      }`,
    )
    .json();
}

export async function uploadSupplementaryBidRequestFiles(
  id: string | undefined,
  version: number | undefined,
  opportunityId: string | undefined,
  formData: FormData,
): Promise<void> {
  return api
    .post(`opportunities/${opportunityId}/bid-requests/${id}/${version}/supplementary-files`, {
      body: formData,
    })
    .json();
}

export interface BidRequestQueryReturn extends BidRequestRecord {
  editDisabled: boolean;
  maxVersionAborted: boolean;
  maxVersionCompleted: boolean;
  maxVersion: number;
}

export async function getBidRequest(
  id: string | undefined | null,
  version?: number | null,
): Promise<BidRequestQueryReturn> {
  const versionParam = version !== undefined ? `/${version}` : '';
  const urlPostfix = `${id}${versionParam}`;
  return api.get(`opportunities/bid-requests/${urlPostfix}`).json();
}

export async function getBidRequestConfigs(
  id: string | undefined,
  version: number | undefined,
  opportunityId: string | undefined,
): Promise<{ configs: Array<ConfigsPerScenario> }> {
  return api.get(`opportunities/${opportunityId}/bid-requests/${id}/${version}/configs`).json();
}

export interface BidRequestProjectPrefillResponse {
  project: BidRequestProjectRecord | null;
  source_bid_request: Pick<BidRequestRecord, 'uuid' | 'name' | 'version'> | null;
}

export type BidRequestProjectPrefillForm = 'dev' | 'txm';

export async function getBidRequestProjectPrefill(
  id: string | undefined,
  version: number | undefined,
  projectId: string | null,
  form: BidRequestProjectPrefillForm,
): Promise<BidRequestProjectPrefillResponse> {
  return api
    .get(`opportunities/bid-requests/${id}/${version}/projects/${projectId}/prefill`, {
      searchParams: { form },
    })
    .json();
}

async function getBidRequests({
  id,
  opportunityId,
  search,
  projectIds,
  status,
  onlyMe,
  staleOnly,
  limit = 100,
  offset = 0,
  completionStatus = 'all',
  latestVersionOnly = true,
}: Omit<BidRequestQueryOptions, 'name'> & { search?: string }): Promise<
  ListResultsQuery<BidRequestsQueryListColumns>
> {
  const searchParams: SearchParamsOption = {
    ...(id ? { id } : {}),
    ...(opportunityId ? { opportunityId } : {}),
    ...(search ? { name: search } : {}),
    ...(projectIds ? { projectIds } : {}),
    ...(status ? { status } : {}),
    ...(onlyMe ? { onlyMe } : {}),
    ...(staleOnly ? { staleOnly } : {}),
    limit,
    offset,
    completionStatus,
    latestVersionOnly,
  };
  return api.get('opportunities/bid-requests', { searchParams }).json();
}

export const useBidRequestsQuery = (
  params: Omit<BidRequestQueryOptions, 'name'> & { search?: string },
  opts?: QueryOptions<ListResultsQuery<BidRequestsQueryListColumns>>,
) =>
  useQuery<ListResultsQuery<BidRequestsQueryListColumns>>({
    queryKey: ['bid-requests', ...Object.values(params)],
    queryFn: () => getBidRequests(params),
    placeholderData: keepPreviousData,
    staleTime: 30e3, // 30 seconds
    ...opts,
  });

async function createBidRequest(data: NewBidRequestPayload, precheckOnly?: boolean): Promise<UUID> {
  return api
    .post(`opportunities/${data.opportunity_id}/bid-requests`, {
      searchParams: { precheckOnly: precheckOnly ?? false },
      json: data,
    })
    .json();
}

export async function approveBidRequest(
  id: string | undefined,
  version: number,
  opportunityId: string | undefined,
  meta: BidRequestInputApprovalsPayload,
): Promise<void> {
  return api
    .post(`opportunities/${opportunityId}/bid-requests/${id}/${version}/approve`, { json: meta })
    .json();
}

export async function editBidRequest(
  id: string | undefined,
  version: number | undefined,
  opportunityId: string | undefined,
  data: EditBidRequestInputsPayload,
  precheckOnly?: boolean,
): Promise<BidRequestRecord | BidRequestStatusOnEdit> {
  return api
    .post(`opportunities/${opportunityId}/bid-requests/${id}/${version}`, {
      json: data,
      searchParams: precheckOnly ? { precheckOnly } : undefined,
    })
    .json();
}

export const useAddConfigsMutation = (
  id: string | undefined,
  version: number | undefined,
  opportunityId: string | undefined,
  opts?: MutationOptions<void, HTTPError, EditConfigsPayload>,
) =>
  useMutation({
    mutationFn: (configs) =>
      api
        .post(`opportunities/${opportunityId}/bid-requests/${id}/${version}/configs`, {
          json: configs,
        })
        .json(),
    ...opts,
  });

export const useEditProcurementInputsMutation = (
  id: string | undefined,
  version: number | undefined,
  opportunityId: string | undefined,
  opts?: MutationOptions<BidRequestRecord, HTTPError, EditProcurementInputsPayload>,
) =>
  useMutation({
    mutationFn: (payload) =>
      api
        .post(`opportunities/${opportunityId}/bid-requests/${id}/${version}/procurement-inputs`, {
          json: payload,
        })
        .json<BidRequestRecord>(),
    ...opts,
  });

export async function createPricingScenariosInSf(
  id: string | undefined,
  version: number | undefined,
  opportunityId: string | undefined,
  payload: NewSfPricingScenarioRecordPayload,
): Promise<SFObjectUpdateResponse> {
  return api
    .post(`opportunities/${opportunityId}/bid-requests/${id}/${version}/pricing`, {
      json: payload,
    })
    .json();
}

export const getBidRequestEngFolder = async (
  id: string | undefined,
  version: number | undefined,
  opportunityId: string | undefined,
): Promise<{ path: string }> =>
  api.get(`opportunities/${opportunityId}/bid-requests/${id}/${version}/eng-folder`).json();

export const getBidRequestPricingFolder = async (
  id: string | undefined,
  version: number | undefined,
  opportunityId: string | undefined,
): Promise<{ path: string }> =>
  api.get(`opportunities/${opportunityId}/bid-requests/${id}/${version}/pricing-folder`).json();

export const addDevInputsToBidRequest = async (
  id: string | undefined,
  version: number | undefined,
  opportunityId: string | undefined,
): Promise<AddFileResponse> =>
  api
    .post(`opportunities/${opportunityId}/bid-requests/${id}/${version}/dev-inputs`, {
      timeout: 60e3,
    })
    .json();

export const addEngInputsToBidRequest = async (
  id: string | undefined,
  version: number | undefined,
  opportunityId: string | undefined,
): Promise<AddFileResponse> =>
  api.post(`opportunities/${opportunityId}/bid-requests/${id}/${version}/eng-inputs`).json();

export const useAddEngInputsMutation = (
  uuid: string | undefined,
  version: number | undefined,
  opportunityId: string | undefined,
  opts?: MutationOptions<AddFileResponse, HTTPError>,
) =>
  useMutation({
    mutationFn: () =>
      api
        .post(`opportunities/${opportunityId}/bid-requests/${uuid}/${version}/eng-inputs`)
        .json<AddFileResponse>(),
    retry: false,
    ...opts,
  });

export const useSimulateScenariosMutation = (
  uuid: string | undefined,
  version: number | undefined,
  opportunityId: string | undefined,
  opts?: MutationOptions<
    PaMultiConfigSimResponse,
    HTTPError,
    { request: MultiConfigPowerAlphaRequest; bidMatrixIds: Array<number> }
  >,
) =>
  useMutation({
    mutationFn: (req) =>
      api
        .post(
          `opportunities/${opportunityId}/bid-requests/${uuid}/${version}/eng-inputs/simulate`,
          {
            json: { multiConfigRequest: req.request, bidMatrixIds: req.bidMatrixIds },
            timeout: 5 * 60 * 1e3,
          },
        )
        .json(),
    ...opts,
  });

const repriceBidRequest = async (
  id: string | undefined,
  version: number | undefined,
  data: NewBidRequestPayload,
  precheckOnly?: boolean,
): Promise<BidRequestRecord> =>
  api
    .post(`opportunities/${data.opportunity_id}/bid-requests/${id}/${version}/reprice`, {
      searchParams: { precheckOnly: precheckOnly ?? false },
      json: data,
    })
    .json();

export const abortBidRequest = async (
  id: string | undefined,
  opportunityId: string | undefined,
): Promise<void> => api.post(`opportunities/${opportunityId}/bid-requests/${id}/abort`).json();

export const completeBidRequest = async (
  id: string | undefined,
  version: number | undefined,
  opportunityId: string | undefined,
): Promise<void> =>
  api.post(`opportunities/${opportunityId}/bid-requests/${id}/${version}/complete`).json();

export const readPricingScenarioUploadTemplate = async (
  id: string | undefined,
  version: number | undefined,
  opportunityId: string | undefined,
  form: FormData,
): Promise<Array<Partial<PricingScenarioRecord>>> =>
  api
    .post(
      `opportunities/${opportunityId}/bid-requests/${id}/${version}/pricing-scenario-template`,
      {
        body: form,
      },
    )
    .json();

export const getPricingScenarios = async (
  id: string | undefined,
  version: number | undefined,
  opportunityId: string | undefined,
): Promise<Array<Partial<PricingScenarioRecord>>> =>
  api.get(`opportunities/${opportunityId}/bid-requests/${id}/${version}/pricing-scenarios`).json();

export function useBidRequestEngFolderQuery(
  id: string | undefined,
  version: number | undefined,
  opportunityId: string | undefined,
  opts?: QueryOptions<{ path: string }>,
) {
  return useQuery<{ path: string }>({
    queryKey: ['bid-request-folder', id, version],
    queryFn: () => getBidRequestEngFolder(id, version, opportunityId),
    ...opts,
    enabled:
      (opts?.enabled ?? true) && Boolean(id) && version !== undefined && Boolean(opportunityId),
  });
}

export const useBidRequestTeamsQuery = (
  opportunityId: string | undefined,
  uuid?: string | undefined,
  version?: number | undefined,
  opts?: QueryOptions<TeamsPerProject> & { addedProjects?: Array<string> },
) =>
  useQuery<TeamsPerProject>({
    queryKey: ['project-teams', opportunityId, uuid, version, ...(opts?.addedProjects ?? [])],
    queryFn: () => getBidRequestTeams(opportunityId, uuid, version ?? 0, opts?.addedProjects),
    staleTime: hoursToMilliseconds(24),
    ...opts,
    enabled: (opts?.enabled ?? true) && Boolean(uuid ?? opts?.addedProjects?.length),
  });

export const getBidRequestConfigsQueryKey = (
  id: string | undefined,
  version: number | undefined,
) => ['bid-request-configs', id, version];

export function useBidRequestConfigsQuery(
  id: string | undefined,
  version: number | undefined,
  opportunityId: string | undefined,
  opts?: QueryOptions<{ configs: Array<ConfigsPerScenario> }>,
) {
  return useQuery<{ configs: Array<ConfigsPerScenario> }>({
    queryKey: getBidRequestConfigsQueryKey(id, version),
    queryFn: () => getBidRequestConfigs(id, version, opportunityId),
    ...opts,
    enabled:
      (opts?.enabled ?? true) && Boolean(id) && version !== undefined && Boolean(opportunityId),
  });
}

export const getBidRequestProjectPrefillQueryKey = (
  id: string | undefined,
  version: number | undefined,
  projectId: string | null,
  form: BidRequestProjectPrefillForm,
) => ['bid-request-project-prefill', id, version, projectId, form];

export const useBidRequestProjectPrefillQuery = (
  id: string | undefined,
  version: number | undefined,
  projectId: string | null,
  form: BidRequestProjectPrefillForm,
  opts?: QueryOptions<BidRequestProjectPrefillResponse>,
) =>
  useQuery<BidRequestProjectPrefillResponse>({
    queryKey: getBidRequestProjectPrefillQueryKey(id, version, projectId, form),
    queryFn: () => getBidRequestProjectPrefill(id, version, projectId, form),
    staleTime: Number.POSITIVE_INFINITY,
    ...opts,
    enabled: (opts?.enabled ?? true) && Boolean(id) && version !== undefined && Boolean(projectId),
  });

export const getPricingScenariosQueryKey = (
  id: string | undefined,
  version: number | undefined,
) => ['pricing-scenarios', id, version];
export const usePricingScenariosQuery = (
  id: string | undefined,
  version: number | undefined,
  opportunityId: string | undefined,
  opts?: QueryOptions<Array<Partial<PricingScenarioRecord>>>,
) =>
  useQuery<Array<Partial<PricingScenarioRecord>>>({
    queryKey: getPricingScenariosQueryKey(id, version),
    queryFn: () => getPricingScenarios(id, version, opportunityId),
    ...opts,
    enabled:
      (opts?.enabled ?? true) && Boolean(id) && version !== undefined && Boolean(opportunityId),
  });

export interface BidRequestMeta extends BidRequestQueryReturn, BidRequestState {}

export const useBidRequestQuery = (
  id: string | undefined | null,
  version?: number | null,
  opts?: QueryOptions<BidRequestMeta>,
) =>
  useQuery<BidRequestMeta>({
    queryKey: ['bid-request', id, version],
    queryFn: () => getBidRequest(id, version).then(getBidRequestState),
    ...opts,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: (opts?.enabled ?? true) && Boolean(id),
  });

export const useNewBidRequestMutation = (
  repriceId: string | undefined | null,
  repriceVersionInt: number | undefined,
  opts?: MutationOptions<
    UUID | BidRequestStatusOnEdit,
    HTTPError,
    { data: NewBidRequestPayload; precheckOnly?: boolean }
  >,
) =>
  useMutation<
    UUID | BidRequestStatusOnEdit,
    HTTPError,
    { data: NewBidRequestPayload; precheckOnly?: boolean }
  >({
    mutationFn: ({ data: bidRequest, precheckOnly }) =>
      repriceId
        ? repriceBidRequest(repriceId, repriceVersionInt, bidRequest, precheckOnly)
        : createBidRequest(bidRequest, precheckOnly),
    ...opts,
    retry: 1, // sometimes SharePoint has forbidden errors on folder creation
  });

export const useEngRunsStatusQuery = (
  runIds: Array<string>,
  opts?: QueryOptions<ListResultsQuery<FlowRunStatus>, HTTPError, ListResultsQuery<FlowRunStatus>>,
) =>
  useQuery({
    queryKey: ['get-eng-input-run-status', ...runIds],
    queryFn: () => getRunsStatus({ runIds }),
    ...opts,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(runIds.length) && (opts?.enabled ?? true),
  });

export const useOpportunitiesListQuery = (
  opts?: QueryOptions<Array<OpportunityListEntry>> & {
    apiOptions?: {
      search?: string;
      stage?: string;
      showClosedLost?: boolean;
    };
  },
) =>
  useQuery({
    queryKey: ['opportunities-list', ...objectToQueryKey(opts?.apiOptions)],
    queryFn: () => {
      const searchParams: SearchParamsOption = {
        showClosedLost: opts?.apiOptions?.showClosedLost,
        ...(opts?.apiOptions?.search ? { name: opts.apiOptions.search } : {}),
        ...(opts?.apiOptions?.stage ? { stage: opts.apiOptions.stage } : {}),
      };
      return api.get('opportunities', { searchParams }).json<Array<OpportunityListEntry>>();
    },
    staleTime: minutesToMilliseconds(30),
    ...opts,
  });

export const useProjectPricingGlobalDataQuery = <T = PricingDashboardResponse>(
  opts?: QueryOptions<T> & {
    apiOptions?: PricingDashboardQueryOptions;
    then?: (data: PricingDashboardResponse) => T;
  },
) =>
  useQuery<T>({
    queryKey: ['project-pricing-global-data', ...objectToQueryKey(opts?.apiOptions)],
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: its ok
    queryFn: () => {
      const apiOptions = opts?.apiOptions;
      const searchParams: SearchParamsOption = {
        ...(apiOptions?.search ? { search: apiOptions.search } : {}),
        limit: apiOptions?.limit ?? 100,
        offset: apiOptions?.offset ?? 0,
        ...(apiOptions?.plant_type ? { plant_type: apiOptions.plant_type } : {}),
        ...(apiOptions?.transaction ? { transaction: apiOptions.transaction } : {}),
        ...(apiOptions?.status ? { status: apiOptions.status } : {}),
        ...(apiOptions?.region ? { region: apiOptions.region } : {}),
        ...(apiOptions?.project_id ? { project_id: apiOptions.project_id } : {}),
        ...(apiOptions?.sort_by ? { sort_by: apiOptions.sort_by } : {}),
        ...(apiOptions?.sort_direction ? { sort_direction: apiOptions.sort_direction } : {}),
        ...(apiOptions?.updated_at ? { updated_at: apiOptions.updated_at } : {}),
      };
      return api
        .get('pricing-dashboard', { searchParams })
        .json<PricingDashboardResponse>()
        .then(opts?.then);
    },
    staleTime: minutesToMilliseconds(1), // 1min
    ...opts,
  });
